import React, { useState, useCallback } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Divider,
  FormLabel,
  IconButton,
  List,
  ListItem,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  DataUsage as NetTrafficIcon,
  DeleteOutline as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { TimeField } from '@mui/x-date-pickers';

import { callApi } from 'utils';
import { Page } from 'components';
import { NetTraffic } from './components';

const range = (start, stop) => Array.from({length: stop-start+1}, (_, i) => i+start);
function getNextDays(n) {
  const d = new Date();
  return range(1, n).map(() => {
    d.setDate(d.getDate() + 1);
    return [d.getMonth()+1, d.getDate()].map(n => (''+n).padStart(2, '0')).join('-');
  });
}

const defaultData = {op: 'inc', ants: 0, rlrs: 0, atdate: 'now', time: null};

const Cloudboard = () => {
  const [data, setData] = useState(defaultData);
  const [saving, setSaving] = useState(false);
  const [atJobs, setAtJobs] = useState();
  const [relayers, setRelayers] = useState();
  const [instance, setInstance] = useState('');

  const handleChange = useCallback((ev) => setData(prev => ({...prev, [ev.target.name]: ev.target.value})), []);
  const handleChangeTime = useCallback((val) => setData(prev => ({...prev, time: val})), []);

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (saving) {
      return false;
    }
    setSaving(true);

    const data_ = {
      ...data,
      time: data.time?.format('HH:mm'),
    };
    callApi('/systems/opcloud', 'POST', data_)
      .then((res) => {
        console.log(res);
        setData(defaultData);
        setSaving(false);
      }).catch(() => setSaving(false));
  };

  const refreshAtJobs = useCallback(() => callApi('/systems/atjobs').then(setAtJobs).catch(console.error), []);
  const refreshRelayers = useCallback(() => callApi('/systems/relayers').then(setRelayers).catch(console.error), []);
  const deleteAtJob = (id) => callApi('/systems/atjobs/'+id, 'DELETE').then(setAtJobs).catch(console.error);
  const viewNetTraffic = useCallback((id) => setInstance(id), []);
  const closeModal = useCallback(() => setInstance(''), []);

  return (
    <Page title="Cloudboard">
      <Paper sx={{p: 2, m: 2}}>
        <Box sx={{ my: 1, display: "flex", alignItems: "center", "& > *": {mx: 0.5} }}>
          <Select
            name="op"
            value={data.op}
            size="small"
            onChange={handleChange}
            sx={{ '& .MuiSelect-select': {py: '6px'} }}
          >
            <MenuItem value="inc">Increase</MenuItem>
            <MenuItem value="dec">Decrease</MenuItem>
          </Select>
          <FormLabel gutterBottom>ants {data.op === 'inc' ? 'by' : 'to'}</FormLabel>
          <OutlinedInput
            sx={{padding: 0, width: 65}}
            name="ants"
            type="number"
            value={data.ants}
            size="small"
            inputProps={{ style: {padding: '6px'} }}
            onChange={handleChange}
          />
          <FormLabel gutterBottom>relayers by</FormLabel>
          <OutlinedInput
            sx={{padding: 0, width: 65}}
            name="rlrs"
            type="number"
            value={data.rlrs}
            size="small"
            inputProps={{ style: {padding: '6px'} }}
            onChange={handleChange}
          />
          <FormLabel gutterBottom>at</FormLabel>
          <Select
            name="atdate"
            value={data.atdate}
            size="small"
            onChange={handleChange}
            sx={{ '& .MuiSelect-select': {py: '6px'} }}
          >
            <MenuItem value="now">now</MenuItem>
            <MenuItem value="today">today</MenuItem>
            {getNextDays(10).map(d => (<MenuItem value={d} key={d}>{d}</MenuItem>))}
          </Select>
          {data.atdate !== 'now' && (
          <TimeField
            sx={{
              mx: 0.5,
              '& .MuiInputBase-root': {width: '55px'},
              '& .MuiInputBase-input': {p: '5px'},
            }}
            format="HH:mm"
            value={data.time}
            onChange={handleChangeTime}
          />)}
          <Button
            color="primary"
            variant="outlined"
            size="small"
            onClick={handleSubmit}
            disabled={saving}
            sx={{ mx: 0.5 }}
          >
            {saving ? "Setting..." : "Set"}
          </Button>
        </Box>

        <Divider sx={{my: 2}} />
        <Typography component="h3">
          Scheduled at jobs
          <IconButton onClick={refreshAtJobs} size="small">
            <RefreshIcon fontSize="inherit" />
          </IconButton>
        </Typography>
      {atJobs === undefined ? (
        <Typography component="div">Hit refresh to check</Typography>
      ) : atJobs.length === 0 ? (
        <Typography component="div">No scheduled jobs</Typography>
      ) : (
        <List sx={{ width: '100%', maxWidth: 300 }}>
        {atJobs.map(row => (
          <ListItem
            key={row.id}
            secondaryAction={
              <IconButton edge="end" onClick={() => deleteAtJob(row.id)} size="small">
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            }
            disablePadding
          >
            {row.text}
          </ListItem>
        ))}
        </List>
      )}
        <Divider sx={{my: 2}} />
        <Typography component="h3">
          Extra relayers
          <Typography component="span" sx={{mx: 0.5}}>({relayers?.length || 0})</Typography>
          <IconButton onClick={refreshRelayers} size="small">
            <RefreshIcon fontSize="inherit" />
          </IconButton>
        </Typography>
      {relayers === undefined ? (
        <Typography component="div">Hit refresh to check</Typography>
      ) : relayers.length === 0 ? (
        <Typography component="div">No extra relayers</Typography>
      ) : (
        <Table size="small" sx={{ '& th,td': { textAlign: 'center' } }}>
          <TableHead>
            <TableRow>
              <TableCell>InstanceName</TableCell>
              <TableCell>CreationTime</TableCell>
              <TableCell>InstanceId</TableCell>
              <TableCell>BandwidthOut</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {relayers.map(row => (
            <TableRow key={row.InstanceId}>
              <TableCell>{row.InstanceName}</TableCell>
              <TableCell>{dayjs(row.CreationTime)?.format('MM-DD HH:mm')}</TableCell>
              <TableCell>{row.InstanceId}</TableCell>
              <TableCell>{row.InternetMaxBandwidthOut}</TableCell>
              <TableCell>{row.Status}</TableCell>
              <TableCell>
                <IconButton onClick={() => viewNetTraffic(row.InstanceId)} size="small">
                  <NetTrafficIcon fontSize="inherit" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          </TableBody>
        </Table>
      )}
      </Paper>
    {instance !== '' && (
      <NetTraffic instance={instance} onClose={closeModal} />
    )}
    </Page>
  );
};

export default Cloudboard;
